import type {AppendMessage} from '@assistant-ui/core';
import type {
  HttpUserPrompt,
  PromptPart,
} from '@wandb/weave/lib/WbAgent/promptParts';

import {auiContentToStoreParts} from './messageConversions';
import {buildPromptContextParts, type PromptContextParams} from './pageContext';
import {
  type ContentPart,
  type PagePromptContent,
  type WorkspacePageContent,
} from './store/thread';

/**
 * BuiltPrompt is the frontend representation of a user prompt produced by the
 * composer (or reconstructed from store content for re-runs). It pairs the
 * backend shape (`httpParts`) with the store shape (`storeParts`) so callers
 * can both POST to `/turns` and optimistically render in the message tree
 * without re-deriving either from the other.
 */
export interface BuiltPrompt {
  httpParts: HttpUserPrompt;
  storeParts: ContentPart[];
  plainText: string;
}

/**
 * buildAssembledPrompt produces the full wire prompt a turn would POST for the
 * given user parts and route: the same context parts + user parts the composer
 * sends, converted to their backend shape. For preview/debugging only.
 */
export function buildAssembledPrompt(
  userStoreParts: ContentPart[],
  params: PromptContextParams
): HttpUserPrompt {
  return withPromptContext(builtPromptFromStoreContent(userStoreParts), params)
    .httpParts;
}

/**
 * withPromptContext prepends nav/page context parts (URL, entity/project/run
 * scope, page-content slices) to the user's prompt. Applied once per user
 * message at the runtime boundary so every entry point (composer, suggestions,
 * any future quick-action) carries the same context.
 */
export function withPromptContext(
  userBuilt: BuiltPrompt,
  params: PromptContextParams
): BuiltPrompt {
  const contextParts = buildPromptContextParts(params);
  return builtPromptFromStoreContent([
    ...contextParts,
    ...userBuilt.storeParts,
  ]);
}

/**
 * readBuiltPrompt derives a {@link BuiltPrompt} from an AUI `AppendMessage`.
 * AUI `content` is the sole source of truth: the composer emits structured
 * parts (text + `data` parts for wandb mentions)
 */
export function readBuiltPrompt(message: AppendMessage): BuiltPrompt | null {
  const storeParts = auiContentToStoreParts(
    message.content as ReadonlyArray<{type: string; [k: string]: unknown}>
  );
  if (storeParts.length === 0) {
    return null;
  }
  return builtPromptFromStoreContent(storeParts);
}

/** extractText extracts plain text from an AUI `AppendMessage`'s content parts. */
export function extractText(message: AppendMessage): string {
  return (message.content as readonly {type: string; text?: string}[])
    .filter(p => p.type === 'text')
    .map(p => p.text ?? '')
    .join('\n\n');
}

/** builtPromptFromText builds a {@link BuiltPrompt} from plain text. */
export function builtPromptFromText(text: string): BuiltPrompt {
  return {
    httpParts: text,
    storeParts: [{type: 'text', text, status: 'complete'}],
    plainText: text,
  };
}

/**
 * builtPromptFromStoreContent builds a {@link BuiltPrompt} from store content
 * parts, deriving the backend shape and a plain-text rendering.
 */
export function builtPromptFromStoreContent(parts: ContentPart[]): BuiltPrompt {
  const httpParts: PromptPart[] = [];
  const plainTextChunks: string[] = [];

  for (const p of parts) {
    switch (p.type) {
      case 'text':
        httpParts.push({type: 'text', text: p.text});
        plainTextChunks.push(p.text);
        break;
      case 'image':
        httpParts.push({
          type: 'image',
          media_type: p.mediaType,
          base64_data: p.base64Data,
        });
        break;
      case 'wandb-entity':
        httpParts.push({type: 'wandb_entity', entity_name: p.entityName});
        break;
      case 'wandb-project':
        httpParts.push({
          type: 'wandb_project',
          entity_name: p.entityName,
          project_name: p.projectName,
        });
        break;
      case 'wandb-run':
        httpParts.push({
          type: 'wandb_run',
          entity_name: p.entityName,
          project_name: p.projectName,
          run_name: p.runName,
        });
        plainTextChunks.push(`@${p.runDisplayName ?? p.runName}`);
        break;
      case 'wandb-call':
        httpParts.push({
          type: 'wandb_call',
          entity_name: p.entityName,
          project_name: p.projectName,
          call_id: p.callId,
        });
        break;
      case 'wandb-web-page-state':
        httpParts.push({
          type: 'wandb_web_page_state',
          url: p.url,
          entity_name: p.entityName,
          project_name: p.projectName,
          // NOTE: the BE page-state schema accepts only workspace content, so
          // report entries drop here until that schema gains a report variant.
          content: p.content?.filter(isWorkspacePageContent).map(c => ({
            type: 'workspace',
            view_id: c.viewId,
            view_name: c.viewName,
            view_type: c.viewType,
            view_scope: c.viewScope,
            run_count_total: c.runCountTotal,
            run_count_filtered: c.runCountFiltered,
            run_count_visualized: c.runCountVisualized,
            run_filters_expression: c.runFiltersExpression,
            panel_search_query: c.panelSearchQuery,
            run_selector_grouping: c.runSelectorGrouping,
          })),
        });
        break;
      case 'unknown':
        // Unknown parts are dropped on retransmission.
        // Silently dropping now, but can add logs if needed.
        break;
    }
  }
  return {
    httpParts: httpParts.length > 0 ? httpParts : '',
    storeParts: parts,
    plainText: plainTextChunks.join('').trim(),
  };
}

function isWorkspacePageContent(
  content: PagePromptContent
): content is WorkspacePageContent {
  return content.type === 'workspace';
}
